import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import {
  FeedbackSnackbar,
  useIframeResizer,
  finishProcessing,
  ProgressModal,
} from './common';
import { config } from '../../../config';

interface PreviewState {
  videoId: string | null;
  thumbnailUrl: string | null;
  title: string | null;
  description: string | null;
  author: string | null;
  viewCount: string | null;
  uploadDate: string | null;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const isUserMessage = (role: Message['role']): role is 'user' =>
  role === 'user';

const AskYouTube: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const playerRef = useRef<HTMLIFrameElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [inputUrl, setInputUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewState>({
    videoId: null,
    thumbnailUrl: null,
    title: null,
    description: null,
    author: null,
    viewCount: null,
    uploadDate: null,
  });
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [questionCount, setQuestionCount] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  useIframeResizer();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const extractYouTubeId = (videoUrl: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];

    const match = patterns.find((pattern) => videoUrl.match(pattern));
    return match ? videoUrl.match(match)?.[1] || null : null;
  };

  useEffect(() => {
    const fetchVideoInfo = async () => {
      const videoId = extractYouTubeId(inputUrl);
      if (videoId) {
        try {
          const response = await fetch(
            `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`
          );
          const data = await response.json();
          setPreview({
            videoId,
            thumbnailUrl: `https://img.youtube.com/vi/${videoId}/default.jpg`,
            title: data.title || null,
            description: data.description || null,
            author: data.author_name || null,
            viewCount: data.view_count?.toLocaleString() || null,
            uploadDate: data.upload_date || null,
          });
        } catch {
          setPreview({
            videoId,
            thumbnailUrl: `https://img.youtube.com/vi/${videoId}/default.jpg`,
            title: null,
            description: null,
            author: null,
            viewCount: null,
            uploadDate: null,
          });
        }
      } else {
        setPreview({
          videoId: null,
          thumbnailUrl: null,
          title: null,
          description: null,
          author: null,
          viewCount: null,
          uploadDate: null,
        });
      }
    };
    fetchVideoInfo();
  }, [inputUrl]);

  const handleNewChat = () => {
    setInputUrl('');
    setMessages([]);
    setCurrentMessage('');
    setQuestionCount(0);
    setError(null);
    setPreview({
      videoId: null,
      thumbnailUrl: null,
      title: null,
      description: null,
      author: null,
      viewCount: null,
      uploadDate: null,
    });
  };

  const handleImport = () => {
    if (preview.videoId) {
      setIsImporting(true);
      setImportProgress(0);
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 10;
        setImportProgress(Math.min(currentProgress, 100));
        if (currentProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            finishProcessing(
              `https://www.youtube.com/watch?v=${preview.videoId}`
            );
          }, 2000);
        }
      }, 20);
    }
  };

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || !preview.videoId || isLoading) return;

    setIsLoading(true);
    const newUserMessage: Message = {
      role: 'user',
      content: currentMessage,
    };
    setMessages([...messages, newUserMessage]);
    setCurrentMessage('');

    try {
      if (questionCount >= 1) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: `[Click here to ask more questions](https://www.youtube.com/watch?v=${preview.videoId}) about this video. I'll help you analyze it in detail.`,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        const response = await fetch(config.geminiProxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: 'gemini-1.5-flash-8b',
            contents: [
              {
                parts: [
                  {
                    text: `You are a helpful AI assistant that answers questions about YouTube videos. You have watched and understood this video completely.

Video Information:
Title: ${preview.title || 'Untitled Video'}
Channel: ${preview.author || 'Unknown Channel'}
${preview.uploadDate ? `Upload Date: ${preview.uploadDate}` : ''}
${preview.viewCount ? `Views: ${preview.viewCount}` : ''}
URL: https://www.youtube.com/watch?v=${preview.videoId}

${preview.description ? `Description:\n${preview.description}\n` : ''}

User Question: ${currentMessage}

Please provide a detailed, specific response as if you've actually watched the video. Include relevant details, quotes, or timestamps if applicable. Be conversational but informative, and make references to specific parts of the video when relevant.`,
                  },
                ],
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to get response from API');
        }

        const data = await response.json();
        if (data.candidates && data.candidates[0]) {
          const assistantMessage: Message = {
            role: 'assistant',
            content: data.candidates[0].content.parts[0].text,
          };
          setMessages((prev) => [...prev, assistantMessage]);
        }
      }
      setQuestionCount((prev) => prev + 1);
    } catch (err) {
      setError('Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviewPadding = () => {
    if (!preview.videoId) return '14px';
    if (isSmallScreen || isMediumScreen) return '48px';
    return '150px';
  };

  const getPreviewMaxWidth = () => {
    if (isSmallScreen || isMediumScreen) return '32px';
    return preview.title ? '250px' : '130px';
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: 'auto',
        p: { xs: 2, sm: 3 },
        fontFamily: 'Inter',
      }}
    >
      <Box sx={{ position: 'relative', mb: { xs: 2, sm: 3 } }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('askyoutube.input.placeholder')}
          value={inputUrl}
          onChange={(e) => setInputUrl(e.target.value)}
          disabled={isLoading}
          helperText={
            !preview.videoId ? t('askyoutube.input.supportText') : undefined
          }
          FormHelperTextProps={{
            sx: {
              mt: 1,
              ml: 0,
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
              fontFamily: 'Inter',
              pr: getPreviewPadding(),
              fontSize: { xs: '0.875rem', sm: '1rem' },
              transition: 'all 0.2s ease-in-out',
            },
          }}
          InputProps={{
            startAdornment: !preview.videoId && (
              <YouTubeIcon
                sx={{
                  color: 'text.secondary',
                  mr: 1,
                  fontSize: '1.25rem',
                }}
              />
            ),
          }}
        />
        {preview.videoId && (
          <Paper
            elevation={0}
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 0.5,
              pr: isSmallScreen || isMediumScreen ? 0.5 : 0,
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              maxWidth: getPreviewMaxWidth(),
              height: '32px',
              overflow: 'hidden',
              zIndex: 1,
            }}
          >
            {preview.thumbnailUrl ? (
              <Box
                component="img"
                src={preview.thumbnailUrl}
                alt="Video thumbnail"
                sx={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'cover',
                  borderRadius: '2px',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '2px',
                }}
              >
                <YouTubeIcon sx={{ fontSize: 16, color: '#FF0000' }} />
              </Box>
            )}
            {!isSmallScreen && !isMediumScreen && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '10px',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {preview.title || 'YouTube Video'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setInputUrl('')}
                  sx={{
                    p: 0.5,
                    mr: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            )}
          </Paper>
        )}
      </Box>

      {preview.videoId && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '56.25%',
              bgcolor: 'background.default',
            }}
          >
            <iframe
              ref={playerRef}
              src={`https://www.youtube.com/embed/${preview.videoId}?enablejsapi=1`}
              title={t('askyoutube.input.playerTitle')}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              maxHeight: '400px',
              overflowY: 'auto',
            }}
          >
            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent:
                    message.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 2,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: '70%',
                    p: 2,
                    bgcolor: isUserMessage(message.role)
                      ? 'primary.main'
                      : 'background.default',
                    color: isUserMessage(message.role)
                      ? 'primary.contrastText'
                      : 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  {isUserMessage(message.role) ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        '& p': { m: 0 },
                        '& p:not(:last-child)': { mb: 1 },
                        '& a': {
                          color: isUserMessage(message.role)
                            ? 'inherit'
                            : 'primary.main',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        },
                        '& ul': {
                          pl: 2,
                          mb: 1,
                        },
                      }}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.tagName === 'A') {
                          e.preventDefault();
                          handleImport();
                        }
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </Box>
                  )}
                </Paper>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              gap: 1,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('askyoutube.input.askPlaceholder')}
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.default',
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!currentMessage.trim() || isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      )}

      {messages.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ChatIcon />}
            onClick={handleNewChat}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'rgba(156, 39, 176, 0.04)',
              },
            }}
          >
            {t('askyoutube.messages.newChat')}
          </Button>
        </Box>
      )}

      <FeedbackSnackbar
        open={!!error}
        message={error || t('askyoutube.messages.error')}
        severity="error"
        onClose={() => setError(null)}
      />

      <ProgressModal
        open={isImporting}
        progress={importProgress}
        isComplete={importProgress === 100}
        processingMessage={t('importURL.processing')}
        completeMessage={t('importURL.complete')}
        onReset={() => {
          setIsImporting(false);
          setImportProgress(0);
        }}
        onSaveAgain={handleImport}
      />
    </Box>
  );
};

export default AskYouTube;
